import React, { useRef } from "react";
import styled from "styled-components";
import { FiPlus, FiTrash } from "react-icons/fi";

import CustomSelect from "../utils/CustomSelect";

const SHIRT_SIZES = [
  ["stockS", "S"],
  ["stockM", "M"],
  ["stockL", "L"],
  ["stockXL", "XL"],
  ["stockSM", "S/M"],
  ["stockLXL", "L/XL"],
];

const SOCK_SIZES = [
  ["stockSocksS", "39–42"],
  ["stockSocksM", "43–46"],
];

/**
 * Zajednička forma za kreiranje i izmenu proizvoda — create i edit izgledaju
 * i ponašaju se identično; razlikuje se samo rukovanje slikama (lokalne vs.
 * već otpremljene), što stranice rešavaju kroz callback-ove.
 */
const ProductForm = ({
  form,
  onInput,
  categories = [],
  refs = {},
  thumbnailUrl,
  onThumbnailFile,
  onRemoveThumbnail,
  galleryImages = [],
  onGalleryFiles,
  onRemoveGalleryImage,
  submitLabel = "Sačuvaj",
  loading = false,
  onSubmit,
}) => {
  const thumbInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  const numberInput = (field) => (e) =>
    onInput(field, e.target.value === "" ? "" : Number(e.target.value));

  return (
    <Wrap>
      <Grid>
        <div>
          <Section>
            <SectionTitle>Osnovni podaci</SectionTitle>

            <Field>
              <Label>Naziv proizvoda</Label>
              <Input
                ref={refs.nameRef}
                placeholder="npr. Muscle Stringer"
                value={form.name}
                onChange={(e) => onInput("name", e.target.value)}
              />
            </Field>

            <Field>
              <Label>Opis</Label>
              <Textarea
                ref={refs.descriptionRef}
                placeholder="Kratak opis proizvoda…"
                value={form.description}
                onChange={(e) => onInput("description", e.target.value)}
              />
            </Field>

            <Row>
              <Field>
                <Label>Kategorija</Label>
                <CustomSelect
                  value={form.category}
                  onChange={(v) => onInput("category", v)}
                  placeholder="Izaberi kategoriju…"
                  options={categories}
                />
              </Field>
              <Field>
                <Label>Badge (opciono)</Label>
                <Input
                  placeholder='npr. "Novo", "Akcija"'
                  value={form.badge}
                  onChange={(e) => onInput("badge", e.target.value)}
                />
              </Field>
            </Row>
          </Section>

          <Section>
            <SectionTitle>Cena</SectionTitle>

            <ToggleRow
              $active={form.comingSoon}
              onClick={() => onInput("comingSoon", !form.comingSoon)}
            >
              <ToggleDot $active={form.comingSoon} />
              Uskoro stiže (bez cene, ne može da se kupi)
            </ToggleRow>

            {!form.comingSoon && (
              <Row>
                <Field>
                  <Label>Cena (RSD)</Label>
                  <Input
                    ref={refs.priceRef}
                    placeholder="npr. 2900"
                    type="number"
                    value={form.price}
                    onChange={numberInput("price")}
                  />
                </Field>
                <Field>
                  <Label>Cena sa popustom (opciono)</Label>
                  <Input
                    placeholder="npr. 2500"
                    type="number"
                    value={form.salePrice}
                    onChange={numberInput("salePrice")}
                  />
                </Field>
              </Row>
            )}
          </Section>

          <Section>
            <SectionTitle>Varijanta</SectionTitle>

            <Row>
              <Field>
                <Label>Naziv boje</Label>
                <Input
                  ref={refs.colorStringRef}
                  placeholder="npr. Crna"
                  value={form.colorString}
                  onChange={(e) => onInput("colorString", e.target.value)}
                />
              </Field>
              <Field>
                <Label>Boja (hex)</Label>
                <ColorRow>
                  <ColorPreview $color={form.color} />
                  <Input
                    ref={refs.colorRef}
                    placeholder="#000000"
                    value={form.color}
                    onChange={(e) => onInput("color", e.target.value)}
                    style={{ flex: 1 }}
                  />
                </ColorRow>
              </Field>
            </Row>
          </Section>

          <Section>
            <SectionTitle>Stanje</SectionTitle>

            <Field>
              <Label>Majice</Label>
              <StockGrid>
                {SHIRT_SIZES.map(([key, label]) => (
                  <StockBox key={key} $active={Number(form[key]) > 0}>
                    <b>{label}</b>
                    <input
                      type="number"
                      min="0"
                      placeholder="0"
                      value={form[key]}
                      onChange={(e) => onInput(key, e.target.value)}
                    />
                  </StockBox>
                ))}
              </StockGrid>
            </Field>

            <Field>
              <Label>Čarape</Label>
              <StockGrid $narrow>
                {SOCK_SIZES.map(([key, label]) => (
                  <StockBox key={key} $active={Number(form[key]) > 0}>
                    <b>{label}</b>
                    <input
                      type="number"
                      min="0"
                      placeholder="0"
                      value={form[key]}
                      onChange={(e) => onInput(key, e.target.value)}
                    />
                  </StockBox>
                ))}
              </StockGrid>
            </Field>

            <Hint>
              Unesi broj komada po veličini — 0 znači da veličina nije
              dostupna. Kad sve padne na 0, proizvod je automatski rasprodat, a
              stanje se skida pri svakoj porudžbini.
            </Hint>
          </Section>
        </div>

        <ImagesCol>
          <Section>
            <SectionTitle>Slike</SectionTitle>

            <Field>
              <Label>Glavna slika</Label>
              {thumbnailUrl ? (
                <ThumbPreview>
                  <img src={thumbnailUrl} alt="thumbnail" />
                  <ThumbActions>
                    <SmallBtn onClick={() => thumbInputRef.current.click()}>
                      Zameni
                    </SmallBtn>
                    {onRemoveThumbnail && (
                      <SmallBtn $danger onClick={onRemoveThumbnail}>
                        <FiTrash size={13} />
                      </SmallBtn>
                    )}
                  </ThumbActions>
                </ThumbPreview>
              ) : (
                <BigUploadTile onClick={() => thumbInputRef.current.click()}>
                  <FiPlus size={22} />
                  <span>Dodaj glavnu sliku</span>
                </BigUploadTile>
              )}
              <HiddenInput
                type="file"
                accept="image/*"
                ref={thumbInputRef}
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) onThumbnailFile(file);
                  e.target.value = "";
                }}
              />
            </Field>

            <Field>
              <Label>Galerija</Label>
              <GalleryGrid>
                {galleryImages.map((img) => (
                  <ImageItem key={img.id}>
                    <img src={img.url} alt="galerija" />
                    <DeleteIcon onClick={() => onRemoveGalleryImage(img)}>
                      <FiTrash size={14} />
                    </DeleteIcon>
                  </ImageItem>
                ))}
                <UploadTile onClick={() => galleryInputRef.current.click()}>
                  <FiPlus size={18} />
                  <span>Dodaj</span>
                </UploadTile>
              </GalleryGrid>
              <HiddenInput
                type="file"
                accept="image/*"
                multiple
                ref={galleryInputRef}
                onChange={(e) => {
                  const files = Array.from(e.target.files);
                  if (files.length) onGalleryFiles(files);
                  e.target.value = "";
                }}
              />
            </Field>
          </Section>
        </ImagesCol>
      </Grid>

      <SubmitBtn $disabled={loading} onClick={loading ? undefined : onSubmit}>
        {loading ? (
          <>
            {submitLabel}
            <l-line-spinner
              size="16"
              stroke="2"
              speed="1"
              color="#fe5f55"
              style={{ position: "absolute", right: "2rem" }}
            />
          </>
        ) : (
          submitLabel
        )}
      </SubmitBtn>
    </Wrap>
  );
};

export default ProductForm;

const Wrap = styled.div`
  width: 100%;
  max-width: 110rem;
  background: var(--bg1, #101013);
  border: 1px solid var(--line, rgba(255, 255, 255, 0.07));
  border-radius: 0.6rem;
  padding: 2.8rem;
  margin-bottom: 4rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 30rem;
  gap: 4rem;
  align-items: start;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
    gap: 0;
  }
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 2.2rem 0;
  border-top: 1px solid var(--line, rgba(255, 255, 255, 0.07));

  &:first-child {
    padding-top: 0;
    border-top: none;
  }
`;

const ImagesCol = styled.div`
  @media (max-width: 980px) {
    ${Section} {
      border-top: 1px solid var(--line, rgba(255, 255, 255, 0.07));
      padding-top: 2.2rem;
    }
  }
`;

const SectionTitle = styled.h3`
  font-family: "Anton", sans-serif;
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-size: 1.6rem;
  color: var(--text, #f4f2ee);
  display: flex;
  align-items: center;
  gap: 1.2rem;

  &::before {
    content: "";
    width: 1.8rem;
    height: 1px;
    background: var(--red, #fe5f55);
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  flex: 1;
  min-width: 0;
`;

const Row = styled.div`
  display: flex;
  gap: 1.4rem;

  @media (max-width: 640px) {
    flex-direction: column;
  }
`;

const Label = styled.label`
  font-family: "Poppins-Medium";
  font-size: 1.1rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--muted, #8e8c94);
`;

const Input = styled.input`
  padding: 1.1rem 1.3rem;
  height: 4.4rem;
  background: var(--bg0, #0a0a0c);
  border: 1px solid var(--line2, rgba(255, 255, 255, 0.14));
  color: var(--text, #f4f2ee);
  border-radius: 0.4rem;
  font-family: "Poppins-Regular";
  font-size: 1.3rem;
  outline: none;
  transition: border-color 0.25s;

  &::placeholder {
    color: var(--muted2, #5c5a63);
  }

  &:focus {
    border-color: var(--red, #fe5f55);
  }
`;

const Textarea = styled.textarea`
  padding: 1.1rem 1.3rem;
  background: var(--bg0, #0a0a0c);
  border: 1px solid var(--line2, rgba(255, 255, 255, 0.14));
  color: var(--text, #f4f2ee);
  border-radius: 0.4rem;
  font-family: "Poppins-Regular";
  font-size: 1.3rem;
  min-height: 9rem;
  outline: none;
  resize: vertical;
  transition: border-color 0.25s;

  &::placeholder {
    color: var(--muted2, #5c5a63);
  }

  &:focus {
    border-color: var(--red, #fe5f55);
  }
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.3rem;
  border-radius: 0.4rem;
  cursor: pointer;
  user-select: none;
  font-family: "Poppins-Medium";
  font-size: 1.3rem;
  transition: all 0.25s;
  width: fit-content;
  border: 1px solid
    ${({ $active }) =>
      $active
        ? "rgba(254, 95, 85, 0.5)"
        : "var(--line2, rgba(255,255,255,0.14))"};
  background: ${({ $active }) =>
    $active ? "rgba(254, 95, 85, 0.08)" : "var(--bg0, #0a0a0c)"};
  color: ${({ $active }) =>
    $active ? "var(--red, #fe5f55)" : "var(--muted, #8e8c94)"};
`;

const ToggleDot = styled.div`
  width: 1.4rem;
  height: 1.4rem;
  border-radius: 50%;
  flex-shrink: 0;
  transition: all 0.25s;
  border: 2px solid
    ${({ $active }) =>
      $active ? "var(--red, #fe5f55)" : "var(--muted2, #5c5a63)"};
  background: ${({ $active }) =>
    $active ? "var(--red, #fe5f55)" : "transparent"};
`;

const ColorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const ColorPreview = styled.span`
  width: 2.6rem;
  height: 2.6rem;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${(props) => props.$color || "transparent"};
  border: 2px solid
    ${(props) =>
      props.$color
        ? "rgba(255, 255, 255, 0.7)"
        : "var(--line2, rgba(255,255,255,0.14))"};
`;

const StockGrid = styled.div`
  display: grid;
  grid-template-columns: ${(props) =>
    props.$narrow
      ? "repeat(auto-fill, minmax(10rem, 12rem))"
      : "repeat(auto-fill, minmax(8.6rem, 1fr))"};
  gap: 0.8rem;
  width: 100%;
`;

const StockBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.9rem;
  border-radius: 0.4rem;
  border: 1px solid
    ${({ $active }) =>
      $active
        ? "rgba(254, 95, 85, 0.45)"
        : "var(--line2, rgba(255,255,255,0.14))"};
  background: ${({ $active }) =>
    $active ? "rgba(254, 95, 85, 0.06)" : "var(--bg0, #0a0a0c)"};
  transition: all 0.2s;

  b {
    font-family: "Poppins-SemiBold";
    font-size: 1.1rem;
    letter-spacing: 0.06em;
    color: ${({ $active }) =>
      $active ? "var(--red, #fe5f55)" : "var(--muted, #8e8c94)"};
    text-align: center;
  }

  input {
    width: 100%;
    background: transparent;
    border: none;
    outline: none;
    color: var(--text, #f4f2ee);
    font-family: "Poppins-SemiBold";
    font-size: 1.4rem;
    text-align: center;
    -moz-appearance: textfield;

    &::placeholder {
      color: var(--muted2, #5c5a63);
    }

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }
`;

const Hint = styled.p`
  font-family: "Poppins-Regular";
  font-size: 1.2rem;
  line-height: 1.6;
  color: var(--muted2, #5c5a63);
`;

const HiddenInput = styled.input`
  display: none;
`;

const BigUploadTile = styled.button`
  width: 100%;
  aspect-ratio: 4 / 5;
  max-height: 26rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.8rem;
  background: var(--bg0, #0a0a0c);
  border: 1px dashed var(--line2, rgba(255, 255, 255, 0.14));
  border-radius: 0.4rem;
  color: var(--muted, #8e8c94);
  cursor: pointer;
  transition: all 0.25s;

  span {
    font-family: "Poppins-Medium";
    font-size: 1.1rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  &:hover {
    border-color: var(--red, #fe5f55);
    color: var(--red, #fe5f55);
  }
`;

const ThumbPreview = styled.div`
  position: relative;
  width: 100%;
  border-radius: 0.4rem;
  overflow: hidden;
  border: 1px solid var(--line2, rgba(255, 255, 255, 0.14));

  img {
    width: 100%;
    aspect-ratio: 4 / 5;
    max-height: 26rem;
    object-fit: cover;
    display: block;
  }
`;

const ThumbActions = styled.div`
  position: absolute;
  bottom: 0.8rem;
  right: 0.8rem;
  display: flex;
  gap: 0.6rem;
`;

const SmallBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.7rem 1.2rem;
  font-family: "Poppins-SemiBold";
  font-size: 1.05rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background: rgba(6, 6, 8, 0.75);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 0.4rem;
  color: #fff;
  cursor: pointer;
  transition: all 0.25s;

  &:hover {
    background: ${(props) =>
      props.$danger ? "var(--red, #fe5f55)" : "rgba(6, 6, 8, 0.9)"};
    border-color: var(--red, #fe5f55);
    color: ${(props) => (props.$danger ? "var(--bg0, #0a0a0c)" : "#fff")};
  }
`;

const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.8rem;
`;

const ImageItem = styled.div`
  position: relative;
  aspect-ratio: 4 / 5;
  border-radius: 0.4rem;
  overflow: hidden;
  border: 1px solid var(--line2, rgba(255, 255, 255, 0.14));

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const DeleteIcon = styled.button`
  position: absolute;
  top: 0.4rem;
  right: 0.4rem;
  background: rgba(6, 6, 8, 0.7);
  color: white;
  border: none;
  border-radius: 50%;
  width: 2.4rem;
  height: 2.4rem;
  cursor: pointer;
  transition: 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: var(--red, #fe5f55);
    color: var(--bg0, #0a0a0c);
  }
`;

const UploadTile = styled.button`
  aspect-ratio: 4 / 5;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  background: var(--bg0, #0a0a0c);
  border: 1px dashed var(--line2, rgba(255, 255, 255, 0.14));
  border-radius: 0.4rem;
  color: var(--muted, #8e8c94);
  cursor: pointer;
  transition: all 0.25s;

  span {
    font-family: "Poppins-Medium";
    font-size: 1rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  &:hover {
    border-color: var(--red, #fe5f55);
    color: var(--red, #fe5f55);
  }
`;

const SubmitBtn = styled.button`
  margin-top: 2.4rem;
  padding: 1.5rem;
  width: 100%;
  max-width: 36rem;
  border-radius: 0.4rem;
  font-family: "Anton", sans-serif;
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-size: 1.3rem;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  transition: background 0.3s var(--ease, ease);

  color: ${(props) =>
    props.$disabled ? "var(--muted, #8e8c94)" : "var(--bg0, #0a0a0c)"};
  background: ${(props) =>
    props.$disabled ? "rgba(23, 23, 28, 0.85)" : "var(--red, #fe5f55)"};
  border: 1px solid
    ${(props) =>
      props.$disabled ? "var(--line2, rgba(255,255,255,0.14))" : "transparent"};
  cursor: ${(props) => (props.$disabled ? "wait" : "pointer")};

  &:hover {
    background: ${(props) =>
      props.$disabled ? "rgba(23, 23, 28, 0.85)" : "var(--red-deep, #e04a40)"};
  }
`;
